import { useRef, useState } from "react";
import type { MacroSnippet } from "@/core/types";
import { Icon } from "./Icon";

interface MacroPanelProps {
  macro: MacroSnippet;
  /** Nur nötig, wenn die Office-Version die Angabe nicht selbst liefert. */
  nurErsatz?: boolean;
}

const TASTEN: Record<MacroSnippet["host"], string> = {
  Word: "Word",
  Excel: "Excel",
  PowerPoint: "PowerPoint",
};

/**
 * Zeigt das Prüfmakro einer Aufgabe samt Anleitung und Kopierschaltfläche.
 *
 * Die Zwischenablage-API ist im Office-Panel nicht überall freigegeben. Klappt
 * `navigator.clipboard` nicht, markiert die Komponente den Code, sodass er mit
 * Strg + C kopiert werden kann.
 */
export function MacroPanel({ macro, nurErsatz = false }: MacroPanelProps) {
  const codeRef = useRef<HTMLPreElement>(null);
  const [status, setStatus] = useState<"idle" | "kopiert" | "markiert">("idle");

  const markieren = () => {
    const element = codeRef.current;
    const auswahl = window.getSelection();
    if (!element || !auswahl) return false;
    const bereich = document.createRange();
    bereich.selectNodeContents(element);
    auswahl.removeAllRanges();
    auswahl.addRange(bereich);
    return true;
  };

  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(macro.code);
      setStatus("kopiert");
      return;
    } catch {
      // Weiter mit dem Ersatzweg.
    }
    if (markieren()) {
      try {
        if (document.execCommand("copy")) {
          setStatus("kopiert");
          return;
        }
      } catch {
        // Markierung bleibt stehen, kopiert wird von Hand.
      }
      setStatus("markiert");
    }
  };

  return (
    <details className="disclosure disclosure--inline macro">
      <summary>
        <Icon name="code" />
        <span>
          {nurErsatz ? "Ersatzweg: " : ""}Prüfmakro „{macro.titel}“
        </span>
        <Icon name="chevronDown" className="disclosure__chevron" />
      </summary>
      <div className="disclosure__content">
        {nurErsatz ? (
          <p className="macro__intro">
            Neuere Versionen von {TASTEN[macro.host]} liefern diese Angabe dem Add-in selbst. Nur
            wenn die Prüfung meldet, dass noch kein Prüfbericht vorliegt, brauchst du das Makro.
            Es liest die Angabe aus und legt einen Prüfbericht in der Datei ab – am Inhalt ändert
            es nichts.
          </p>
        ) : (
          <p className="macro__intro">
            Diese Angabe stellt {TASTEN[macro.host]} dem Add-in nicht zur Verfügung. Das Prüfmakro
            liest sie aus und legt einen Prüfbericht in der Datei ab – am Inhalt ändert es nichts.
          </p>
        )}
        <ol className="macro__steps">
          <li>
            <kbd>Alt</kbd> + <kbd>F11</kbd> öffnet den VBA-Editor (Mac: Extras → Makro → Visual
            Basic-Editor).
          </li>
          <li>Einfügen → Modul, dann den Code unten in das leere Modul einfügen.</li>
          <li>
            Den Cursor in <code>{macro.name}</code> setzen und <kbd>F5</kbd> drücken.
          </li>
          <li>Zurück im Panel auf „Aufgabe prüfen“ klicken.</li>
        </ol>
        <div className="macro__code-wrap">
          <div className="macro__toolbar">
            <span className="macro__name">{macro.name}</span>
            <button type="button" className="btn btn--secondary btn--small" onClick={() => void kopieren()}>
              {status === "kopiert" ? <Icon name="check" size={14} /> : <Icon name="copy" size={14} />}
              {status === "kopiert" ? "Kopiert" : "Code kopieren"}
            </button>
          </div>
          <pre ref={codeRef} className="macro__code" tabIndex={0}>
            <code>{macro.code}</code>
          </pre>
        </div>
        {status === "markiert" ? (
          <p className="macro__status" role="status">
            Markiert – jetzt Strg + C drücken.
          </p>
        ) : null}
        <p className="macro__note">
          Beim Speichern als normale Datei fragt {TASTEN[macro.host]}, ob ohne Makros gespeichert
          werden soll. Mit „Ja“ bestätigen: Das Makro entfällt, der Prüfbericht bleibt erhalten.
          Jedes Prüfmakro gehört in ein eigenes Modul.
        </p>
      </div>
    </details>
  );
}
